import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance } from '../App';
import { tf } from '../utils/translateFeature';
import { ArrowLeft, MapPin, Phone, Mail, Globe, Clock, 
  CheckCircle, XCircle, Trash2, Users, Utensils, Accessibility, ParkingCircle
} from 'lucide-react';
import BusinessHistory from '../components/BusinessHistory';
import { formatPlaceName } from '../utils/placeName';

const AdminBusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  console.log('AdminBusinessDetail - user:', user, 'id:', id);

  useEffect(() => {
    console.log('AdminBusinessDetail useEffect - user:', user);
    // Check if user is admin
    if (!user) {
      console.log('No user, waiting...');
      return;
    }
    if (user.role !== 'admin') {
      console.log('Not admin, redirecting...');
      navigate('/admin/login');
      return;
    }
    fetchBusiness();
  }, [id, user]);

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      console.log('Fetching business:', id);
      const response = await axiosInstance.get(`/admin/business/${id}`);
      console.log('Got business:', response.data);
      setBusiness(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching business:', error);
      setError('Business not found');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') {
      return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">Pending Approval</span>;
    }
    if (status === 'approved') {
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Approved</span>;
    }
    return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">Rejected</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 mb-4">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          {getStatusBadge(business.approvalStatus)}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Photos */}
        {business.photos && business.photos.length > 0 && (
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {business.photos.map((photo, index) => (
                <div
                  key={index}
                  className={`bg-gray-50 rounded-xl shadow-md overflow-hidden ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                >
                  <img
                    src={photo}
                    alt={`${business.businessName} photo ${index + 1}`}
                    className="w-full h-full object-contain aspect-[3/4]"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header section */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              {business.businessName}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                {business.category}
              </span>
              {business.venueType?.map(v => (
                <span key={v} className="px-3 py-1 bg-white/10 rounded-full text-sm">{tf(t, v)}</span>
              ))}
            </div>
          </div>

          {/* Details section */}
          <div className="p-6 space-y-6">
            {/* Description */}
            {(business.description_en || business.description_es) && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('about')}</h2>
                <p className="text-gray-600 whitespace-pre-wrap">{i18n.language.startsWith('es') ? business.description_es : business.description_en}</p>
              </div>
            )}

            {/* Features Tags */}
            {(business.additionalFeatures?.length > 0 || business.accessibilityFeatures?.length > 0 || business.dietaryOptions?.length > 0) && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('featuresAmenities')}</h2>
                <div className="flex flex-wrap gap-2">
                  {business.additionalFeatures?.map(f => (
                    <span key={f} className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm">{tf(t, f)}</span>
                  ))}
                  {business.accessibilityFeatures?.map(f => (
                    <span key={f} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">{tf(t, f)}</span>
                  ))}
                  {business.dietaryOptions?.map(d => (
                    <span key={d} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm">{tf(t, d)}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Group Sizes */}
            {business.groupSizes?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('groupSizesWelcome')}</h2>
                <div className="flex flex-wrap gap-2">
                  {business.groupSizes.map(size => (
                    <span key={size} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">
                      <Users className="h-4 w-4" />
                      {tf(t, size)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Event Types */}
            {business.eventTypes?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('eventTypesLabel')}</h2>
                <div className="flex flex-wrap gap-2">
                  {business.eventTypes.map(type => (
                    <span key={type} className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm">{tf(t, type)}</span>
                  ))}
                </div>
              </div>
            )}

            <hr className="border-gray-200" />

            {/* Contact Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('contactInfo')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MapPin className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">{business.address || 'N/A'}</p>
                    <p className="text-gray-600">{business.city ? formatPlaceName(business.city) : 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Phone className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-gray-900">{business.phone || 'N/A'}</span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Mail className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-gray-900">{business.email}</span>
                </div>

                {business.website && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Globe className="h-5 w-5 text-purple-600" />
                    </div>
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                      {business.website}
                    </a>
                  </div>
                )}

                {business.openingHours && (
                  <div className="flex items-start gap-3 md:col-span-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Clock className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Opening Hours</p>
                      <p className="text-gray-600 whitespace-pre-line text-sm">{business.openingHours.replace(/; /g, '\n')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Subscription Info</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-medium text-gray-900 capitalize">{business.subscriptionStatus}</p>
                </div>
                <div>
                  <p className="text-gray-500">Plan</p>
                  <p className="font-medium text-gray-900 capitalize">{business.subscriptionPlan || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Subscription End</p>
                  <p className="font-medium text-gray-900">{business.subscriptionEnd ? new Date(business.subscriptionEnd).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium text-gray-900">{new Date(business.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Edit History */}
            <BusinessHistory businessId={business.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBusinessDetail;
